import React, { useEffect, useMemo, useState } from 'react';
import { Copy, Check, ExternalLink, RefreshCw } from 'lucide-react';
import api from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from '../components/ui/use-toast';
import { slugify } from './preChatFormStructure';

/** Match backend normalize_flow_name: letters, digits, underscores. */
function normalizeFlowName(raw: string): string {
  let cleaned = (raw || '')
    .trim()
    .replace(/[^A-Za-z0-9_]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '');
  if (!cleaned) cleaned = 'Form';
  if (!/^[A-Za-z]/.test(cleaned)) cleaned = `Form_${cleaned}`;
  return cleaned.slice(0, 64);
}

export type PublishableForm = {
  id: number;
  title: string;
  public_slug?: string | null;
  whatsapp_flow_id?: string | null;
  whatsapp_channel_id?: number | null;
};

type InboxRow = {
  id: number;
  name: string;
  channel_type?: string;
  whatsapp_channel_id?: number | null;
  phone_number?: string;
};

type Props = {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  form: PublishableForm | null;
  mode?: 'publish' | 'send';
  onDone?: () => void;
};

/** Absolute public form URL — never produces `//f/...`. */
export function publicFormAbsoluteUrl(slug: string): string {
  const clean = (slug || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .replace(/\/+/g, '');
  const path = `/f/${clean || 'form'}`;
  if (typeof window === 'undefined') return path;
  const origin = window.location.origin.replace(/\/+$/, '');
  const rawBase = (process.env.PUBLIC_URL || '').replace(/\/+$/, '');
  if (!rawBase || rawBase === '/') return `${origin}${path}`;
  if (rawBase.startsWith('http')) return `${rawBase.replace(/\/+$/, '')}${path}`;
  const basePath = rawBase.startsWith('/') ? rawBase : `/${rawBase}`;
  return `${origin}${basePath.replace(/\/+$/, '')}${path}`;
}

const PreChatFormPublishDialog: React.FC<Props> = ({
  open,
  onOpenChange,
  form,
  mode = 'publish',
  onDone,
}) => {
  const [surface, setSurface] = useState<'web' | 'wa'>('web');
  const [slugDraft, setSlugDraft] = useState('');
  const [lockedSlug, setLockedSlug] = useState('');
  const [inboxes, setInboxes] = useState<InboxRow[]>([]);
  const [waChannelId, setWaChannelId] = useState<number | ''>('');
  const [partnerId, setPartnerId] = useState('');
  const [partnerKey, setPartnerKey] = useState('');
  const [showCreds, setShowCreds] = useState(false);
  const [phone, setPhone] = useState('');
  const [bodyText, setBodyText] = useState('Please complete this form.');
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [compat, setCompat] = useState<{ flagged?: any[] } | null>(null);
  const [flowName, setFlowName] = useState('');

  useEffect(() => {
    if (!open || !form) return;
    const existing = (form.public_slug || '').trim();
    setLockedSlug(existing);
    setSlugDraft(existing || slugify(form.title));
    setFlowName(normalizeFlowName(form.title));
    setSurface(mode === 'send' ? 'wa' : 'web');
    setBodyText('Please complete this form.');
    setCopied(false);
    (async () => {
      try {
        const [inboxRes, compatRes] = await Promise.all([
          api.get('/inboxes/'),
          api.get(`/pre-chat-forms/${form.id}/whatsapp-compatibility`),
        ]);
        const raw = inboxRes.data?.inboxes ?? inboxRes.data ?? [];
        const list = Array.isArray(raw) ? raw : [];
        const rows: InboxRow[] = list.filter(
          (i: InboxRow) =>
            (i.channel_type || '').toLowerCase() === 'whatsapp' && Boolean(i.whatsapp_channel_id),
        );
        setInboxes(rows);
        if (form.whatsapp_channel_id) setWaChannelId(form.whatsapp_channel_id);
        else if (rows[0]?.whatsapp_channel_id) setWaChannelId(rows[0].whatsapp_channel_id);
        setCompat(compatRes.data || null);
      } catch (e: any) {
        console.error('Failed to load WhatsApp inboxes', e);
        setInboxes([]);
        toast({
          title: 'Could not load WhatsApp inboxes',
          description: e?.response?.data?.detail || 'Try again',
          variant: 'destructive',
        });
      }
    })();
  }, [open, form, mode]);

  const absoluteUrl = useMemo(
    () => (lockedSlug ? publicFormAbsoluteUrl(lockedSlug) : ''),
    [lockedSlug],
  );

  const displayUrl = useMemo(
    () => publicFormAbsoluteUrl(lockedSlug || slugDraft || 'my-form'),
    [lockedSlug, slugDraft],
  );

  const copyUrl = async () => {
    if (!displayUrl) return;
    try {
      await navigator.clipboard.writeText(displayUrl);
      setCopied(true);
      toast({ title: 'Link copied' });
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      toast({ title: 'Could not copy', variant: 'destructive' });
    }
  };

  const openForm = () => {
    if (!absoluteUrl) return;
    window.open(absoluteUrl, '_blank', 'noopener,noreferrer');
  };

  const publishWeb = async (regenerate: boolean) => {
    if (!form) return;
    setBusy(true);
    try {
      const res = await api.post(`/pre-chat-forms/${form.id}/publish-web`, {
        slug: regenerate || !lockedSlug ? slugDraft : undefined,
        regenerate,
      });
      const nextSlug = res.data?.public_slug || lockedSlug;
      setLockedSlug(nextSlug);
      setSlugDraft(nextSlug);
      const url = publicFormAbsoluteUrl(nextSlug);
      toast({
        title: regenerate ? 'New public link generated' : 'Web form ready',
        description: url,
      });
      onDone?.();
    } catch (e: any) {
      toast({ title: e?.response?.data?.detail || 'Failed to publish web link', variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };

  const publishWa = async (force = false) => {
    if (!form || !waChannelId) {
      toast({ title: 'Select a WhatsApp inbox', variant: 'destructive' });
      return;
    }
    const name = normalizeFlowName(flowName || form.title);
    if (!name) {
      toast({ title: 'Enter a Flow name', variant: 'destructive' });
      return;
    }
    setBusy(true);
    try {
      const res = await api.post(`/pre-chat-forms/${form.id}/publish-whatsapp`, {
        whatsapp_channel_id: waChannelId,
        partner_id: partnerId || undefined,
        partner_api_key: partnerKey || undefined,
        flow_name: name,
        // Always force when a flow already exists — Meta cannot overwrite published JSON
        force_republish: force || !!form.whatsapp_flow_id,
      });
      const publishedName = res.data?.flow_name || name;
      const newId = res.data?.flow_id;
      const nameChanged = !!res.data?.name_changed;
      toast({
        title: force || form.whatsapp_flow_id
          ? 'WhatsApp Flow republished'
          : 'WhatsApp Flow published',
        description: nameChanged
          ? `Meta kept "${name}" reserved — published as "${publishedName}" (id ${newId}). Send Flow uses the new id.`
          : newId
            ? `flow_id: ${newId}`
            : undefined,
      });
      onDone?.();
      if (mode !== 'send') onOpenChange(false);
    } catch (e: any) {
      const detail = e?.response?.data?.detail;
      let message = 'Failed to publish WhatsApp Flow';
      if (typeof detail === 'string') {
        try {
          const parsed = JSON.parse(detail);
          message =
            parsed?.meta?.developer_message ||
            parsed?.error_data?.details ||
            parsed?.message ||
            detail;
        } catch {
          message = detail;
        }
      } else if (detail) {
        message = String(detail);
      }
      toast({ title: message, variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };

  const sendFlow = async () => {
    if (!form) return;
    if (!form.whatsapp_flow_id) {
      toast({ title: 'Publish a WhatsApp Flow first', variant: 'destructive' });
      return;
    }
    if (!phone.trim()) {
      toast({ title: 'Enter a contact phone', variant: 'destructive' });
      return;
    }
    setBusy(true);
    try {
      await api.post(`/pre-chat-forms/${form.id}/send-flow`, {
        phone: phone.trim(),
        body_text: bodyText,
      });
      toast({ title: 'Flow sent' });
      onDone?.();
      onOpenChange(false);
    } catch (e: any) {
      toast({ title: e?.response?.data?.detail || 'Failed to send Flow', variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };

  const flagged = compat?.flagged?.length || 0;
  const hasLockedLink = Boolean(lockedSlug);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{mode === 'send' ? 'Send Flow' : 'Generate / Publish'}</DialogTitle>
          <DialogDescription>
            {form?.title} — same form definition for public web link and WhatsApp Flow.
          </DialogDescription>
        </DialogHeader>

        {mode === 'publish' && (
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              className={`rounded-lg border p-3 text-left text-sm ${surface === 'web' ? 'border-primary bg-primary/10' : 'border-border'}`}
              onClick={() => setSurface('web')}
            >
              <div className="font-semibold">Public web form</div>
              <div className="text-xs text-muted-foreground">Survey-style link</div>
            </button>
            <button
              type="button"
              className={`rounded-lg border p-3 text-left text-sm ${surface === 'wa' ? 'border-primary bg-primary/10' : 'border-border'}`}
              onClick={() => setSurface('wa')}
            >
              <div className="font-semibold">WhatsApp Flow</div>
              <div className="text-xs text-muted-foreground">Publish then send</div>
            </button>
          </div>
        )}

        {mode === 'publish' && surface === 'web' && (
          <div className="space-y-3">
            <div>
              <Label>Public link</Label>
              <div className="mt-1 flex gap-2">
                <Input
                  readOnly
                  value={displayUrl}
                  className="font-mono text-xs"
                  onFocus={(e) => e.target.select()}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="shrink-0"
                  onClick={copyUrl}
                  title="Copy link"
                  disabled={!displayUrl}
                >
                  {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="shrink-0"
                  onClick={openForm}
                  title="Open form"
                  disabled={!hasLockedLink}
                >
                  <ExternalLink className="h-4 w-4" />
                </Button>
              </div>
              <p className="mt-1.5 text-[11px] text-muted-foreground">
                {hasLockedLink
                  ? 'Full URL with domain. Link stays the same until you regenerate. Form is live.'
                  : 'Preview of the full URL (includes your domain). Click Generate to make it live.'}
              </p>
            </div>
            <div>
              <Label>{hasLockedLink ? 'New slug (optional, for regenerate)' : 'Public slug'}</Label>
              <Input
                value={slugDraft}
                onChange={(e) => setSlugDraft(e.target.value)}
                className="mt-1"
                placeholder="my-form"
              />
            </div>
          </div>
        )}

        {(mode === 'send' || surface === 'wa') && (
          <div className="space-y-3">
            {flagged > 0 && (
              <p className="text-xs rounded-md border border-amber-800/50 bg-amber-950/40 text-amber-300 px-3 py-2">
                {flagged} field(s) flagged for WhatsApp — omitted from Flow. Web link unchanged.
              </p>
            )}
            {mode === 'publish' && (
              <>
                <div>
                  <Label>WhatsApp inbox</Label>
                  <select
                    className="mt-1 flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                    value={waChannelId}
                    onChange={(e) => setWaChannelId(e.target.value ? Number(e.target.value) : '')}
                  >
                    <option value="">Select inbox…</option>
                    {inboxes.map((i) => (
                      <option key={i.id} value={i.whatsapp_channel_id!}>
                        {i.name}{i.phone_number ? ` · ${i.phone_number}` : ''}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label>Flow name</Label>
                  <Input
                    value={flowName}
                    onChange={(e) => setFlowName(e.target.value)}
                    onBlur={() => setFlowName((v) => normalizeFlowName(v || form?.title || ''))}
                    className="mt-1 font-mono text-sm"
                    placeholder="My_Form"
                  />
                  <p className="mt-1 text-[11px] text-muted-foreground">
                    Letters, numbers, underscores. Meta cannot overwrite a published Flow — republish creates a new Flow (same name when possible, otherwise <code className="text-[10px]">Name_r…</code>) and updates this form&apos;s flow_id.
                  </p>
                </div>
                <Button type="button" variant="outline" size="sm" onClick={() => setShowCreds((v) => !v)}>
                  {showCreds ? 'Hide' : 'Add'} Partner credentials
                </Button>
                {showCreds && (
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    <div>
                      <Label>Partner ID</Label>
                      <Input value={partnerId} onChange={(e) => setPartnerId(e.target.value)} className="mt-1" />
                    </div>
                    <div>
                      <Label>Partner API key</Label>
                      <Input type="password" value={partnerKey} onChange={(e) => setPartnerKey(e.target.value)} className="mt-1" />
                    </div>
                  </div>
                )}
                {form?.whatsapp_flow_id && (
                  <p className="text-xs text-muted-foreground">
                    Current flow_id: <span className="font-mono">{form.whatsapp_flow_id}</span>
                  </p>
                )}
              </>
            )}
            {(mode === 'send' || form?.whatsapp_flow_id) && (
              <div className="space-y-2 border-t border-border pt-3">
                <div>
                  <Label>Contact phone (E.164)</Label>
                  <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+15550123" className="mt-1" />
                </div>
                <div>
                  <Label>Body / CTA</Label>
                  <Input value={bodyText} onChange={(e) => setBodyText(e.target.value)} className="mt-1" />
                </div>
              </div>
            )}
          </div>
        )}

        <DialogFooter className="gap-2 flex-wrap">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          {mode === 'publish' && surface === 'web' && hasLockedLink && (
            <Button
              variant="outline"
              disabled={busy}
              onClick={() => publishWeb(true)}
            >
              <RefreshCw className="h-4 w-4 mr-1.5" />
              Regenerate link
            </Button>
          )}
          {mode === 'publish' && surface === 'web' && (
            <Button disabled={busy} onClick={() => publishWeb(false)}>
              {hasLockedLink ? 'Ensure live' : 'Generate web link'}
            </Button>
          )}
          {mode === 'publish' && surface === 'wa' && (
            <>
              <Button disabled={busy} variant="outline" onClick={() => publishWa(true)}>
                <RefreshCw className="h-4 w-4 mr-1.5" />
                Force republish
              </Button>
              <Button disabled={busy} onClick={() => publishWa(false)}>Publish Flow</Button>
              {form?.whatsapp_flow_id && (
                <Button disabled={busy} variant="secondary" onClick={sendFlow}>Send Flow</Button>
              )}
            </>
          )}
          {mode === 'send' && (
            <Button disabled={busy} onClick={sendFlow}>Send Flow</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PreChatFormPublishDialog;
